import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import TabCSVImport from '../tabs/TabCSVImport';
import { apiGet, apiDelete } from '../utils/api-client';
import { API_BASE } from '../config/api';
import { storage } from '../utils/platform';
import { useToast } from '../components/Toast';

/**
 * TabCSVImportContainer
 *
 * Endpoints branchés :
 *  - GET    /api/csv-import/imports              → historique des imports
 *  - POST   /api/csv-import/upload (multipart)   → upload CSV
 *  - DELETE /api/csv-import/imports/:importId    → suppression d'un import (Sprint 2 P2)
 */
export default function TabCSVImportContainer() {
  const { t } = useTranslation('tabs');
  const [history, setHistory] = useState([]);
  const toast = useToast();

  const loadHistory = useCallback(async () => {
    try {
      const data = await apiGet('/api/csv-import/imports');
      setHistory(Array.isArray(data?.imports) ? data.imports : Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('[TabCSVImport] history error:', e);
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  /** Upload CSV via multipart/form-data (apiPost ne gère pas FormData). */
  const handleImport = useCallback(async ({ source, file, mapping }) => {
    if (!file) {
      alert(t('csv_import.alerts.no_file'));
      return;
    }
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('source', source || 'custom');
      if (mapping) formData.append('mapping', JSON.stringify(mapping));

      const token = await storage.get('token');
      const res = await fetch(`${API_BASE}/api/csv-import/upload`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Erreur ${res.status}`);

      alert(t('csv_import.alerts.import_success', { count: data.imported || 0 }));
      await loadHistory();
      return data;
    } catch (e) {
      alert(t('csv_import.alerts.upload_failed', { error: e.message }));
    }
  }, [t, loadHistory]);

  /** Télécharger un template CSV (génère côté frontend). */
  const handleDownloadTemplate = useCallback((source) => {
    const headers = ['prenom', 'telephone', 'email', 'date_visite'];
    const exampleRow = ['Jean', '0612345678', 'jean@example.fr', '2026-04-15'];
    const csv = headers.join(',') + '\n' + exampleRow.join(',') + '\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `revyo-template-${source || 'custom'}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }, []);

  /**
   * Supprime un import CSV et tous ses contacts associés (CASCADE côté DB).
   * Sprint 2 P2 — endpoint backend : DELETE /api/csv-import/imports/:importId.
   */
  const handleDeleteImport = useCallback(async (importId) => {
    if (!importId) return;
    const ok = window.confirm(t('csv_import.alerts.delete_confirm'));
    if (!ok) return;
    try {
      const data = await apiDelete(`/api/csv-import/imports/${importId}`);
      // Retire l'import du state local sans recharger l'historique complet.
      setHistory(prev => prev.filter(i => i.id !== importId));
      const n = Number(data?.deleted_rows || 0);
      toast.success(t('csv_import.alerts.delete_success', { count: n }));
    } catch (e) {
      toast.error(t('csv_import.alerts.delete_failed', { error: e.message }));
    }
  }, [t, toast]);

  return (
    <TabCSVImport
      history={history}
      onImport={handleImport}
      onDownloadTemplate={handleDownloadTemplate}
      onDeleteImport={handleDeleteImport}
    />
  );
}
